import styled from 'styled-components';

import Column from 'components/common/Column';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import { ICON_CHECK } from 'assets/icons';
import {
  Items,
  ProductParamInput,
  ScheduleParam,
} from 'containers/ProductRegist/hooks/types';

const StateBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: fit-content;
  height: 24px;
  padding: 0 6px;
  line-height: 24px;

  background: #ffe5c1;
  color: #dd8100;
  border-radius: 6px;
`;

const RadioBox = styled.input<{ isDisable: boolean }>`
  width: 24px;
  height: 24px;
  appearance: none;
  border: 3px solid #eee;
  border-radius: 50%;
  cursor: ${({ isDisable }) => (isDisable ? 'auto' : 'pointer')};

  &:checked {
    border: 8px solid #1b1c1e;
  }
`;

interface Props {
  isMobile: boolean;
  isAppliedAllItems: boolean;
  productParamInput: ProductParamInput;
  scheduleItem: ScheduleParam;
  isDisable: boolean;
  handleSetIsAppliedAllItems: (isAppliedAllItems: boolean) => void;
  handleAddOptions: (item: Items) => void;
  handleAddAllOptions?: (itemList: Items[]) => void;
}

const OptionSelectFormV2 = (props: Props) => {
  const {
    isMobile,
    isAppliedAllItems,
    productParamInput,
    scheduleItem,
    isDisable,
    handleSetIsAppliedAllItems,
    handleAddOptions,
    handleAddAllOptions = () => {},
  } = props;

  /* 
    조건.

    1. 모든 옵션 적용하기로 체크 되어있는 경우 - scheduleItem.appliedAllItems true
    2. 전체 옵션중 중지된 옵션이 있는지 찾는다.
    3. scheduleItem.itemParamIds의 id중 하나의 id라도 요소 status가 "CLOSED"인 경우(=중지된 경우)  ** 판매 종료의 status도 체크 해보기
  */

  /* 
      옵션 3개 생성 -> 모든 옵션 적용하기로 일정 생성 -> 옵션 1개는 중지, 1개는 판매 종료 후 일정 체크시 이슈

      생생된 옵션은 2개(유효한 옵션, 중지된 옵션 한개씩)
      서버에 저장된 선택한 옵션은 3개

    */

  const closedOptionIdList = productParamInput.items
    .filter(item => item.status === 'CLOSED')
    .map(item => item.id);

  const itemParamIds = scheduleItem.itemParamIds ?? [];

  const closedSet = new Set(closedOptionIdList);
  const unclosedIdList = itemParamIds.filter(id => !closedSet.has(id));

  const currentOptionIdList = productParamInput.items.map(item => item.id); // 현재
  const savedOptionIdList = scheduleItem.itemParamIds; // 일정 생성, 수정시 저장했던 옵션id 리스트
  const availableOptionIdList = savedOptionIdList.filter(
    id => !currentOptionIdList.includes(id),
  ); // 사용 가능한 옵션id 리스트 (판매종료된 옵션만 제외)

  const haveClosedOption =
    isDisable && unclosedIdList.length !== unclosedIdList.length;
  // const haveClosedOption =
  //   isDisable && unclosedIdList.length !== availableOptionIdList.length;

  if (isMobile) {
    return (
      <Column align="flex-start" gap="16px">
        <Text color="#414B56" size="18px" weight={600}>
          옵션 선택
        </Text>
        <Row gap="16px">
          <Row gap="8px">
            <RadioBox
              type="radio"
              // checked={isAppliedAllItems}
              checked={isDisable ? !haveClosedOption : isAppliedAllItems}
              isDisable={isDisable}
              onChange={() => {
                if (isDisable) {
                  return;
                }

                handleSetIsAppliedAllItems(true);
              }}
            />
            <Text color="#414B56" size="16px">
              모든 옵션 적용하기
            </Text>
          </Row>
          <Row gap="8px">
            <RadioBox
              type="radio"
              // checked={!isAppliedAllItems}
              checked={isDisable ? haveClosedOption : !isAppliedAllItems}
              isDisable={isDisable}
              onChange={() => {
                if (isDisable) {
                  return;
                }

                handleSetIsAppliedAllItems(false);
              }}
            />
            <Text color="#414B56" size="16px">
              목록에서 선택하기
            </Text>
          </Row>
        </Row>
        {(!isAppliedAllItems || haveClosedOption) && (
          <Column align="flex-start" gap="16px">
            {productParamInput.items.map(item => {
              const optionState = item?.status; // undefined인 경우는 상품을 생성하지도 않은 최초 등록중인 상태

              const isClosed =
                optionState && !['EDITING', 'OPENED'].includes(optionState);

              // const isSelected = scheduleItem.itemParamIds.includes(
              //   item.paramId,
              // );
              const isSelected = unclosedIdList.includes(item.paramId);
              const activeColor = isSelected ? '#414B56' : '#c2c4c8';

              return (
                <Row
                  key={item.id}
                  justifyContent="space-between"
                  style={{ width: '100%' }}
                  onClick={() => {
                    if (isClosed || isDisable) {
                      return;
                    }

                    handleAddOptions(item);
                  }}
                >
                  <Text
                    color={activeColor}
                    size="16px"
                    style={{
                      cursor: isDisable || isClosed ? 'auto' : 'pointer',
                    }}
                  >
                    {item.name}
                  </Text>

                  {isClosed ? (
                    <StateBadge>중지</StateBadge>
                  ) : (
                    <ICON_CHECK
                      color={activeColor}
                      style={{ cursor: isDisable ? 'auto' : 'pointer' }}
                    />
                  )}
                </Row>
              );
            })}
          </Column>
        )}
      </Column>
    );
  }

  return (
    <Row gap="32px" align="flex-start">
      <Text
        color="#414B56"
        size="18px"
        weight={600}
        style={{ marginTop: '5px' }}
      >
        옵션 선택
      </Text>
      <Row gap="16px" align="flex-start">
        <Row
          gap="8px"
          style={{ cursor: isDisable ? 'auto' : 'pointer' }}
          onClick={() => {
            if (isDisable) {
              return;
            }

            handleSetIsAppliedAllItems(true);
          }}
        >
          <RadioBox
            type="radio"
            // checked={isAppliedAllItems}
            checked={isDisable ? !haveClosedOption : isAppliedAllItems}
            isDisable={isDisable}
            onChange={() => {
              if (isDisable) {
                return;
              }

              handleSetIsAppliedAllItems(true);
            }}
          />
          <Text color="#414B56" size="16px">
            모든 옵션 적용하기
          </Text>
        </Row>
        <Row gap="8px" align="flex-start">
          <RadioBox
            type="radio"
            // checked={!isAppliedAllItems}
            checked={isDisable ? haveClosedOption : !isAppliedAllItems}
            isDisable={isDisable}
            onChange={() => {
              if (isDisable) {
                return;
              }

              if (handleAddAllOptions) {
                handleAddAllOptions(productParamInput.items);
              }

              handleSetIsAppliedAllItems(false);
            }}
          />
          <Column
            align="flex-start"
            gap="12px"
            style={{ marginTop: '5px', width: 'auto' }}
          >
            <Text
              color="#414B56"
              size="16px"
              style={{ cursor: isDisable ? 'auto' : 'pointer' }}
              onClick={() => {
                if (isDisable) {
                  return;
                }

                if (handleAddAllOptions) {
                  handleAddAllOptions(productParamInput.items);
                }

                handleSetIsAppliedAllItems(false);
              }}
            >
              목록에서 선택하기
            </Text>

            {/* 목록에서 선택하기 section */}
            {(!isAppliedAllItems || haveClosedOption) && (
              <Column align="flex-start" gap="16px">
                {productParamInput.items.map(item => {
                  const optionState = item?.status; // undefined인 경우는 상품을 생성하지도 않은 최초 등록중인 상태

                  const isClosed =
                    optionState && !['EDITING', 'OPENED'].includes(optionState);

                  // const isSelected = scheduleItem.itemParamIds.includes(
                  //   item.paramId,
                  // );
                  const isSelected = unclosedIdList.includes(item.paramId);
                  const activeColor = isSelected ? '#414B56' : '#c2c4c8';

                  return (
                    <Row
                      key={item.id}
                      justifyContent="space-between"
                      style={{ width: '400px' }}
                      onClick={() => {
                        if (isClosed || isDisable) {
                          return;
                        }

                        handleAddOptions(item);
                      }}
                    >
                      <Text
                        color={activeColor}
                        size="16px"
                        style={{
                          cursor: isDisable || isClosed ? 'auto' : 'pointer',
                        }}
                      >
                        {item.name}
                      </Text>

                      {isClosed ? (
                        <StateBadge>중지</StateBadge>
                      ) : (
                        <ICON_CHECK
                          color={activeColor}
                          style={{ cursor: isDisable ? 'auto' : 'pointer' }}
                        />
                      )}
                    </Row>
                  );
                })}
              </Column>
            )}
          </Column>
        </Row>
      </Row>
    </Row>
  );
};

export default OptionSelectFormV2;
